import { useLocation, useNavigate, useSearchParams } from "react-router";

/** Search param that holds the NAME of the task whose Run modal is open. */
export const RUN_TASK_PARAM = "run";

/**
 * Drives the Run-task modal's open state through a search param on the CURRENT
 * page, so opening the modal owns a history entry.
 *
 * Why not local `useState`: an in-place `useState` modal pushes nothing to
 * history, so browser Back skips past the launching page entirely instead of
 * closing the modal. Encoding the open state as `?run=<taskName>` keeps the
 * launching page's route matched (it stays mounted underneath — no re-mount,
 * and whichever page opened it is automatically the page behind), while Back
 * now pops the pushed entry and just closes the modal.
 *
 * - `openRunTask` PUSHES `?run=<taskName>` — the modal is its own entry, so
 *   Back closes it.
 * - `closeRunTask` REMOVES the param with `replace`, collapsing the modal entry
 *   so an explicit close (Cancel/ESC/outside-click) lands back on the launching
 *   page without a stray forward entry.
 *
 * A successful run navigates away to the activity log with `replace` (see
 * RunTaskModal `replaceOnComplete`), which consumes the same `?run` entry.
 *
 * The next query string is built from the LIVE `window.location.search`, not
 * React Router's `useSearchParams`. RR's committed search string can lag by a
 * render when a param was written earlier in the same tick, whereas
 * window.location is updated synchronously. Building from it means opening or
 * closing the modal never drops a param another control wrote just before — e.g.
 * the metrics timeframe (`?range`/`?from`/`?to` via useUpdateQueryParams). `run`
 * itself is only ever set through this hook's navigate() (and popstate on Back),
 * so RR's view of it IS authoritative and drives the reactive read.
 */
export const useRunTaskParam = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const runTaskName = searchParams.get(RUN_TASK_PARAM);

  const navigateWithRunParam = (
    mutate: (params: URLSearchParams) => void,
    replace: boolean
  ) => {
    const params = new URLSearchParams(window.location.search);
    mutate(params);
    const query = params.toString();
    navigate(
      { pathname: location.pathname, search: query ? `?${query}` : "" },
      { replace }
    );
  };

  const openRunTask = (taskName: string) =>
    navigateWithRunParam(params => params.set(RUN_TASK_PARAM, taskName), false);

  const closeRunTask = () =>
    navigateWithRunParam(params => params.delete(RUN_TASK_PARAM), true);

  return { runTaskName, openRunTask, closeRunTask };
};
